import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import validacaoService from "../services/validacao";
import pdfLayoutService from "../services/pdfLayout";
import { tipoAutorizacaoEnum } from "../utils/constants";
import licencaHeaderImg from "../assets/images/licenca-header.png";
import licencaFooterImg from "../assets/images/licenca-footer.png";
import { formatDateToShow } from "../utils/formatters";

const DOCUMENTO_PREVIEW_FUNDO = "#ffffff";
const DOCUMENTO_PREVIEW_TEXTO = "#212121";
const DOCUMENTO_PREVIEW_BORDA = "rgba(0, 0, 0, 0.23)";
const TIPO_AUTORIZACAO = "autorizacao";
const TIPO_CDNSLAM = "cdnslam";
const TIPO_ISENCAO = "isencao";

const blobToDataUrl = (blob) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

function LinhaInfo({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 0.25, sm: 1 },
        py: 0.5,
      }}
    >
      <Typography
        sx={{
          fontWeight: "bold",
          minWidth: { sm: 220 },
          fontSize: { xs: 13, sm: 14 },
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: { xs: 13, sm: 14 }, whiteSpace: "pre-wrap", flex: 1 }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

function SecaoCondicionante({ titulo, texto }) {
  if (!texto || !texto.trim()) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ fontWeight: "bold", fontSize: 14, mb: 0.5 }}>
        {titulo}
      </Typography>
      <Box
        sx={{
          border: `1px solid ${DOCUMENTO_PREVIEW_BORDA}`,
          p: 1.5,
          borderRadius: 1,
          whiteSpace: "pre-wrap",
          fontSize: 13,
        }}
      >
        {texto}
      </Box>
    </Box>
  );
}

function ValidarDocumento({ tipo }) {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [headerImgSrc, setHeaderImgSrc] = useState(null);
  const [footerImgSrc, setFooterImgSrc] = useState(null);
  const [erro, setErro] = useState("");

  const carregarLayout = useCallback(async () => {
    try {
      const res = await pdfLayoutService.obterLatest();
      const layoutId = res.data?.data;
      if (!layoutId) {
        setHeaderImgSrc(licencaHeaderImg);
        setFooterImgSrc(licencaFooterImg);
        return;
      }
      const [hRes, fRes] = await Promise.all([
        pdfLayoutService.getHeaderImage(layoutId).catch(() => null),
        pdfLayoutService.getFooterImage(layoutId).catch(() => null),
      ]);
      setHeaderImgSrc(
        hRes ? await blobToDataUrl(hRes.data) : licencaHeaderImg
      );
      setFooterImgSrc(
        fRes ? await blobToDataUrl(fRes.data) : licencaFooterImg
      );
    } catch {
      setHeaderImgSrc(licencaHeaderImg);
      setFooterImgSrc(licencaFooterImg);
    }
  }, []);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      setErro("");
      const resultado =
        tipo === TIPO_AUTORIZACAO
          ? await validacaoService.validarAutorizacao(id)
          : tipo === TIPO_CDNSLAM
          ? await validacaoService.validarCdnslam(id)
          : tipo === TIPO_ISENCAO
          ? await validacaoService.validarIsencao(id)
          : await validacaoService.validarLicenca(id);

      if (!resultado) {
        setErro("Documento não encontrado.");
        setDados(null);
      } else {
        setDados(resultado);
      }
    } catch (e) {
      setErro(e?.message || "Erro ao consultar o documento.");
    } finally {
      setLoading(false);
    }
  }, [id, tipo]);

  useEffect(() => {
    carregarLayout();
    carregarDados();
  }, [carregarLayout, carregarDados]);

  const isAutorizacao = tipo === TIPO_AUTORIZACAO;
  const isCdnslam = tipo === TIPO_CDNSLAM;
  const isIsencao = tipo === TIPO_ISENCAO;
  const isCancelada = !isAutorizacao && !isCdnslam && !isIsencao && !!dados?.isCancelada;
  const titulo = isAutorizacao
    ? "AUTORIZAÇÃO AMBIENTAL VÁLIDA"
    : isCdnslam
    ? "CERTIDÃO DECLARATÓRIA (CDNSLAM) VÁLIDA"
    : isIsencao
    ? "REQUERIMENTO DE ISENÇÃO DE LICENCIAMENTO"
    : isCancelada
    ? "LICENÇA AMBIENTAL CANCELADA"
    : "LICENÇA AMBIENTAL VÁLIDA";

  return (
    <Container
      maxWidth="md"
      sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}
    >
      <Paper
        elevation={2}
        sx={{
          overflow: "hidden",
          bgcolor: DOCUMENTO_PREVIEW_FUNDO,
          color: DOCUMENTO_PREVIEW_TEXTO,
        }}
      >
        {headerImgSrc && (
          <Box sx={{ height: { xs: 60, sm: 75, md: 90 }, overflow: "hidden" }}>
            <img
              src={headerImgSrc}
              alt="Cabeçalho do município"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "fill",
                display: "block",
              }}
            />
          </Box>
        )}

        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, minHeight: { xs: 300, md: 400 } }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
              }}
            >
              <CircularProgress />
            </Box>
          ) : erro || !dados ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 300,
                textAlign: "center",
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 64, color: "error.main" }} />
              <Typography variant="h6" component="h1" sx={{ mt: 2, fontWeight: "bold" }}>
                Documento não encontrado
              </Typography>
              <Typography sx={{ mt: 1, color: "text.secondary" }}>
                {erro ||
                  "Não foi possível localizar o documento informado neste sistema."}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 1,
                  pb: 2,
                  borderBottom: "2px solid",
                  borderColor: isCancelada ? "error.main" : "success.main",
                }}
              >
                {isCancelada ? (
                  <ErrorOutlineIcon sx={{ color: "error.main", fontSize: { xs: 28, sm: 32 } }} />
                ) : (
                  <CheckCircleIcon sx={{ color: "success.main", fontSize: { xs: 28, sm: 32 } }} />
                )}
                <Typography
                  variant="h6"
                  component="h1"
                  sx={{
                    fontWeight: "bold",
                    color: isCancelada ? "error.main" : "inherit",
                    fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" },
                  }}
                >
                  {titulo}
                </Typography>
              </Box>

              <Box sx={{ mt: 2 }}>
                {isAutorizacao ? (
                  <>
                    <LinhaInfo
                      label="Tipo de Autorização:"
                      value={tipoAutorizacaoEnum.find((t) => t.value === dados.tipoAutorizacao)?.label ?? dados.tipoAutorizacao}
                    />
                    <LinhaInfo label="Evento:" value={dados.nomeEvento} />
                    <LinhaInfo label="Local:" value={dados.localEvento} />
                    <LinhaInfo
                      label="Data do Evento:"
                      value={formatDateToShow(dados.dataEvento)}
                    />
                  </>
                ) : isCdnslam ? (
                  <>
                    <LinhaInfo label="Empreendimento:" value={dados.nomeEmpreendimento} />
                    <LinhaInfo label="CNPJ:" value={dados.cnpj} />
                    <LinhaInfo label="Data de Emissão:" value={formatDateToShow(dados.dataEmissao)} />
                  </>
                ) : isIsencao ? (
                  <>
                    <LinhaInfo label="Empreendimento:" value={dados.nomeEmpreendimento} />
                    <LinhaInfo label="CNPJ:" value={dados.cnpj} />
                    <LinhaInfo label="Status:" value={dados.status} />
                    <LinhaInfo label="Data de Envio:" value={formatDateToShow(dados.dataEnvio)} />
                    <LinhaInfo
                      label="Assinatura Digital:"
                      value={dados.assinadaDigitalmente ? "Sim" : "Não"}
                    />
                  </>
                ) : (
                  <>
                    <LinhaInfo
                      label="Tipo de Licença:"
                      value={dados.tipoLicenca}
                    />
                    <LinhaInfo
                      label="Empreendimento:"
                      value={dados.empreendimento}
                    />
                    <LinhaInfo
                      label="CNPJ:"
                      value={dados.cnpjEmpreendimento}
                    />
                    <LinhaInfo
                      label="Endereço:"
                      value={dados.enderecoEmpreendimento}
                    />
                    <LinhaInfo
                      label="Número da Licença:"
                      value={dados.numeroDaLicenca}
                    />
                  </>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {!isCdnslam && !isIsencao && (
                <Box>
                  <LinhaInfo
                    label="Solicitante:"
                    value={dados.solicitanteNome}
                  />
                  <LinhaInfo
                    label="Data da Solicitação:"
                    value={formatDateToShow(dados.dataSolicitacao)}
                  />
                  <LinhaInfo
                    label="Data de Emissão:"
                    value={formatDateToShow(dados.dataEmissao)}
                  />
                  <LinhaInfo
                    label="Analista Responsável:"
                    value={dados.analistaNome}
                  />
                  {!isAutorizacao && (
                    <LinhaInfo
                      label="Assinatura Digital:"
                      value={dados.assinadaDigitalmente ? "Sim" : "Não"}
                    />
                  )}
                  <LinhaInfo label="Status:" value={dados.status} />
                </Box>
              )}

              {!isCdnslam && !isIsencao && (
                isAutorizacao ? (
                  <SecaoCondicionante
                    titulo="CONDICIONANTES"
                    texto={dados.condicionantes}
                  />
                ) : (
                  <>
                    <SecaoCondicionante
                      titulo="CONDICIONANTES GERAIS"
                      texto={dados.condicionantesGerais}
                    />
                    <SecaoCondicionante
                      titulo="CONDICIONANTES COM PRAZO"
                      texto={dados.condicionantesComPrazo}
                    />
                    <SecaoCondicionante
                      titulo="CONDICIONANTES ESPECÍFICAS"
                      texto={dados.condicionantesEspecificas}
                    />
                  </>
                )
              )}

              {dados.observacoes && dados.observacoes.trim() && (
                <SecaoCondicionante
                  titulo="OBSERVAÇÕES"
                  texto={dados.observacoes}
                />
              )}
            </>
          )}
        </Box>

        {footerImgSrc && (
          <Box sx={{ height: { xs: 45, sm: 55, md: 60 }, overflow: "hidden" }}>
            <img
              src={footerImgSrc}
              alt="Rodapé do município"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "fill",
                display: "block",
              }}
            />
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default ValidarDocumento;
